import React, { useEffect, useMemo, useState } from "react";
import { FaArrowDown } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import "./AllProjectsHero.css";
import heroBg from "../../../../assets/images/allprojects/allprojectshero/allphero.png";
import { scrollToElementById } from "../../../../utils/scrollToId";
import { resolveMediaUrl } from "../../../../utils/media";
import { getPageContent } from "../../../../api/content";

const fallbackAllProjectsHero = {
  title: "Our success story",
  description:
    "Access certified training programs designed to build skills, advance careers, and support construction professionals at every level. Your future in construction starts here.",
  ctaText: "Explore all",
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceHeroPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") return tryParseJsonObject(raw) || null;
  if (typeof raw !== "object") return null;
  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const findPageSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const AllProjectsHero = ({ content }) => {
  const { i18n } = useTranslation();
  const [apiContent, setApiContent] = useState(null);

  useEffect(() => {
    if (content && typeof content === "object") return undefined;

    let isMounted = true;
    const load = async () => {
      const language = (i18n.language || "en").toLowerCase();
      const pageAliases = ["allprojects", "all-projects", "projects", "allproject"];
      for (const page of pageAliases) {
        try {
          const data = await getPageContent({ page, language });
          if (!data || typeof data !== "object" || !Object.keys(data).length) continue;
          const section =
            findPageSection(data, "hero") ||
            findPageSection(data, "allprojectshero") ||
            findPageSection(data, "allprojects");
          if (section) {
            if (isMounted) setApiContent(section);
            return;
          }
        } catch {
          // try next alias
        }
      }
      if (isMounted) setApiContent(null);
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [content, i18n.language]);

  const view = useMemo(() => {
    const source = content ?? apiContent;
    const merged = coalesceHeroPayload(source);
    const section = hasSectionData(merged) ? merged : fallbackAllProjectsHero;

    return {
      title: pickString(section.title, section.heading) || fallbackAllProjectsHero.title,
      description:
        pickString(section.description, section.body, section.text, section.summary, section.content) ||
        fallbackAllProjectsHero.description,
      ctaText:
        pickString(section.ctaText, section.linkText, section.exploreText, section.buttonText) ||
        fallbackAllProjectsHero.ctaText,
      backgroundImage:
        resolveMediaUrl(
          section.backgroundImage ||
            section.bgImage ||
            section.image ||
            section.heroImage ||
            section.mediaUrl ||
            section.photo
        ) || heroBg,
    };
  }, [content, apiContent]);

  const handleExplore = () => {
    scrollToElementById("all-projects-explore");
  };

  return (
    <section className="allProjectsHero" aria-label="All projects hero">
      <div className="allProjectsHero-bg">
        <img
          src={view.backgroundImage}
          alt=""
          className="allProjectsHero-bgImage"
          draggable={false}
          onError={(e) => {
            if (e.currentTarget.src !== heroBg) {
              e.currentTarget.src = heroBg;
            }
          }}
        />
      </div>

      <div className="allProjectsHero-overlay" aria-hidden="true" />

      <div className="allProjectsHero-inner">
        <h1 className="allProjectsHero-title">{view.title}</h1>

        <p className="allProjectsHero-text">{view.description}</p>

        <button type="button" className="buttongyp allProjectsHero-cta" onClick={handleExplore}>
          <span>{view.ctaText}</span>
          <FaArrowDown className="allProjectsHero-ctaIcon" aria-hidden />
        </button>
      </div>
    </section>
  );
};

export default AllProjectsHero;
